import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, useAction } from '../../api/hooks';
import type { MyAiConnections } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { QueryView } from '../../ui/feedback';
import { ConfirmDialog } from '../../ui/Modal';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';
import { McpSetupGuide } from '../shared/McpSetupGuide';

/** The signed-in person's AI agent sign-ins (one per approved agent connection), each revocable. */
export function AiConnectionsPage() {
  usePageTitle('My AI connections');
  useCrumbs([{ label: 'My AI connections' }]);
  const q = useQuery({
    queryKey: ['my-ai-connections'],
    queryFn: () => api.get<MyAiConnections>('/me/ai-connections'),
  });
  const [revoking, setRevoking] = useState<string | null>(null);
  const revoke = useAction((id: string) => api.post(`/me/ai-connections/${id}/revoke`), {
    success: 'Connection revoked',
    invalidate: [['my-ai-connections']],
    onSuccess: () => setRevoking(null),
  });
  return (
    <div className="page">
      <PageHeader
        title="My AI connections"
        subtitle="Agents you signed in from Claude Code, Codex, Antigravity or another MCP client."
      />
      <QueryView query={q}>
        {(d) => (
          <div className="stack">
            <Card
              title={d.agent?.name ?? 'Your personal agent'}
              subtitle="Your agent can read and work on the projects you can access. Approvals stay with people."
            >
              {d.connections.length === 0 ? (
                <span className="small muted">No connections yet.</span>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Connection</th>
                        <th>State</th>
                        <th>Connected</th>
                        <th>Last used</th>
                        <th>Expires</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.connections.map((c) => (
                        <tr key={c.id}>
                          <td className="ink">{c.label}</td>
                          <td>
                            <StateBadge state={c.state} />
                          </td>
                          <td className="small">
                            <Timestamp value={c.createdAt} dateOnly />
                          </td>
                          <td className="small">
                            <Timestamp value={c.lastUsedAt} relative />
                          </td>
                          <td className="small">
                            <Timestamp value={c.expiresAt} dateOnly />
                          </td>
                          <td>
                            {c.state === 'active' && (
                              <Button size="sm" variant="ghost" onClick={() => setRevoking(c.id)}>
                                Disconnect
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
            <McpSetupGuide
              endpoint={d.mcp.endpoint}
              instructions={d.mcp.instructions}
              mode={{ kind: 'personal' }}
            >
              <span className="small muted">
                Or add it yourself, then sign in when your agent opens this site. For Claude Code:
              </span>
              <pre className="mono small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {`claude mcp add --transport http lixionary-jira ${d.mcp.endpoint}`}
              </pre>
            </McpSetupGuide>
          </div>
        )}
      </QueryView>
      {revoking && (
        <ConfirmDialog
          title="Disconnect this agent?"
          confirmLabel="Yes, disconnect"
          cancelLabel="Keep connection"
          variant="danger"
          onClose={() => setRevoking(null)}
          onConfirm={() => revoke.mutate(revoking)}
          pending={revoke.isPending}
          error={revoke.error}
        >
          <p>
            The agent stops working at once and has to sign in again. Claims it holds expire and need admin
            recovery.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
